import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listKb, type AdminKbProfile } from '../lib/api';
import { theme } from '../theme';

export function KbBrowserPage() {
  const [profiles, setProfiles] = useState<AdminKbProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  useEffect(() => {
    listKb()
      .then((res) => {
        setProfiles(res.profiles);
        setNextCursor(res.nextCursor);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (!nextCursor) return;
    const res = await listKb(nextCursor);
    setProfiles((prev) => [...prev, ...res.profiles]);
    setNextCursor(res.nextCursor);
  };

  return (
    <div>
      <h1>Vehicle Knowledge Base</h1>
      <p style={{ color: theme.text.secondary, fontSize: 13 }}>
        Per-(make/model/year) profiles aggregated from successful scans. Each profile records which
        OBD commands worked, which addresses are dead, and confidence scores for each.
      </p>
      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}
      {loading && <p>Loading…</p>}
      {!loading && profiles.length === 0 && <p>No KB profiles yet.</p>}
      {!loading && profiles.length > 0 && (
        <>
          <table style={{ width: '100%' }}>
            <thead>
              <tr style={{ background: theme.surface.header }}>
                <Th>Vehicle</Th>
                <Th>Key</Th>
                <Th>Samples</Th>
                <Th>Working</Th>
                <Th>Failing</Th>
                <Th>Avg readings/scan</Th>
                <Th>Last updated</Th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr
                  key={p.vehicleKey}
                  style={{ borderBottom: `1px solid ${theme.surface.border}` }}
                >
                  <Td>
                    <Link to={`/kb/${encodeURIComponent(p.vehicleKey)}`}>
                      {p.make ?? '?'} {p.model ?? '?'} {p.modelYear ?? ''}
                    </Link>
                  </Td>
                  <Td
                    style={{
                      fontFamily: theme.fonts.mono,
                      fontSize: 12,
                      color: theme.text.secondary,
                    }}
                  >
                    {p.vehicleKey}
                  </Td>
                  <Td>{p.sampleCount}</Td>
                  <Td>{p.workingCommandCount}</Td>
                  <Td>{p.failingAddressCount}</Td>
                  <Td>
                    {p.signalSetStats ? (
                      <>
                        {p.signalSetStats.averageReadingCount}
                        <span style={{ color: theme.text.tertiary, fontSize: 11 }}>
                          {' '}
                          (last {p.signalSetStats.lastReadingCount})
                        </span>
                      </>
                    ) : (
                      <span style={{ color: theme.text.tertiary }}>—</span>
                    )}
                  </Td>
                  <Td>{new Date(p.lastUpdatedAt).toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          {nextCursor && (
            <button
              onClick={loadMore}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: theme.surface.panel,
                border: `1px solid ${theme.surface.borderStrong}`,
                color: theme.text.primary,
                borderRadius: theme.radius.md,
              }}
            >
              Load more
            </button>
          )}
        </>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', fontSize: 13, ...style }}>{children}</td>;
}
